import { Ng2StateDeclaration } from '@uirouter/angular';
import { Param, StateService, Transition, UIRouter } from '@uirouter/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AbTestService, Accessibility, Analytics, Breadcrumbs, CimaAuthGuidService, CimaCore, CimaStepUpErrorService, CimaToken, CimaUrl, TrialService, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { CmsCore } from 'store/cms/models';
import { AuthGuard, AuthLogout } from 'src/xm/route-guards';
import { ParamType, PASSIVE_OAUTH_BLACKLIST, TransitionHooks } from 'core/constants';

import { BaseComponent } from 'src/xm/base/base';
import { NavPrimaryComponent } from 'shared/nav/primary/primary';
import { XmFooterComponent } from 'shared/footer/footer';

export function AppInit(
    cimaCore: CimaCore,
    accessibility: Accessibility,
    breadcrumb: Breadcrumbs,
    state: StateService,
    transition: Transition,
    trialService: TrialService,
    analytics: Analytics,
    cimsAuthGuidService: CimaAuthGuidService,
    cimaStepUpErrorService: CimaStepUpErrorService): Promise<string> {

    analytics.init();
    breadcrumb.init();
    accessibility.init();
    trialService.init(transition.params().TRIAL_ID);
    cimaStepUpErrorService.init(transition.params().error);
    cimsAuthGuidService.init(transition);

    return cimaCore.loadCimaToken().then((token: string) => {

        const initPassiveOauth: boolean = !PASSIVE_OAUTH_BLACKLIST.some((stateRegex: RegExp) => stateRegex.test(transition.to().name));

        if (initPassiveOauth) {
            cimaCore.tryPassiveAuthLogin();
        }

        return token;
    })
        .catch(() => {
            // if we cannot get a token, then go to downtime
            state.go('downtime');

            return Promise.reject('no token');
        });
}

export function GetAbTests(abTestService: AbTestService): Promise<string | void> {
    return abTestService.loadTarget();
}

export function LoadCmsCore(xmStore: XmStore): Promise<CmsCore> {
    return XmStoreUtil.defaultCatch(xmStore.fetchPromise<CmsCore>(StoreAction.GET_CORE));
}

export function transitionStart(uiRouter: UIRouter): Observable<Transition> {
    return uiRouter.globals.start$.pipe(
        map((transition: Transition) => transition),
        distinctUntilChanged()
    );
}

export function transitionSuccess(uiRouter: UIRouter): Observable<Transition> {
    return uiRouter.globals.success$.pipe(
        map((transition: Transition) => transition),
        distinctUntilChanged()
    );
}

export function paramsChanged(uiRouter: UIRouter): Observable<Param> {
    return uiRouter.globals.params$.pipe(
        map((params: Param) => params),
        distinctUntilChanged()
    );
}

export const XM_ROUTES: Ng2StateDeclaration[] = [
    {
        abstract: true,
        name: 'xm',
        url: '?TRIAL_ID&AdobeNoBueno&cbmya_sso&INTCMP&CMP&cmp&intcmp&acc&os&error',
        resolve: [
            {
                token: 'appInit',
                deps: [ CimaCore, Accessibility, Breadcrumbs, StateService, Transition, TrialService, Analytics, CimaAuthGuidService, CimaStepUpErrorService ],
                resolveFn: AppInit
            },
            {
                token: 'pageDataCore',
                deps: [ XmStore, 'appInit' ],
                resolveFn: LoadCmsCore
            },
            {
                token: 'abTests',
                deps: [ AbTestService ],
                resolveFn: GetAbTests
            },
            {
                token: TransitionHooks.START,
                deps: [ UIRouter ],
                resolveFn: transitionStart
            },
            {
                token: TransitionHooks.SUCCESS,
                deps: [ UIRouter ],
                resolveFn: transitionSuccess
            },
            {
                token: TransitionHooks.PARAMS,
                deps: [ UIRouter ],
                resolveFn: paramsChanged
            }
        ],
        views: {
            '': {
                component: BaseComponent
            },
            'nav@xm': {
                component: NavPrimaryComponent
            },
            'footer@xm': {
                component: XmFooterComponent
            }
        },
        params: {
            TRIAL_ID: {
                type: ParamType.encoded
            },
            cbmya_sso: {
                type: ParamType.query
            },
            acc: {
                type: ParamType.query
            },
            os: {
                type: ParamType.query
            },
            error: {
                type: ParamType.query
            }
        }
    },
    {
        name: 'account.**',
        url: '/',
        loadChildren: () => import('./my-account/module').then((m: typeof import('./my-account/module')) => m.MyAccountModule)
    },
    {
        name: 'login.**',
        url: '/login',
        loadChildren: () => import('./login/module').then((m: typeof import('./login/module')) => m.XmLoginModule)
    },
    {
        name: 'logout',
        url: '/logout',
        resolve: [
            {
                token: 'cimaLogout',
                deps: [ CimaUrl ],
                resolveFn: AuthLogout
            }
        ]
    },
    {
        name: 'error.**',
        url: '/error',
        loadChildren: () => import('./error/error/module').then((m: typeof import('./error/error/module')) => m.ErrorModule)
    },
    {
        name: 'maintenance.**',
        url: '/maintenance',
        loadChildren: () => import('./error/maintenance/module').then((m: typeof import('./error/maintenance/module')) => m.MaintenanceModule)
    },
    {
        name: 'downtime.**',
        url: '/downtime',
        loadChildren: () => import('./error/downtime/module').then((m: typeof import('./error/downtime/module')) => m.DowntimeModule)
    },
    {
        name: 'orion-customer.**',
        url: '/orion-customer',
        loadChildren: () => import('./error/orion-customer/module').then((m: typeof import('./error/orion-customer/module')) => m.OrionCustomerModule)
    },
    {
        name: 'non-xfinity-customer.**',
        url: '/add-xfinity-mobile',
        loadChildren: () => import('./error/non-xfinity-customer/module').then((m: typeof import('./error/non-xfinity-customer/module')) => m.NonCustomerModule)
    },
    {
        name: 'role-based-customer.**',
        url: '/role-based-error',
        loadChildren: () => import('./error/role-based/module').then((m: typeof import('./error/role-based/module')) => m.RoleBasedCustomerModule)
    },
    {
        name: '404.**',
        loadChildren: () => import('./error/404/module').then((m: typeof import('./error/404/module')) => m.PageNotFoundModule)
    },
    {
        name: 'activation-error.**',
        url: '/activate-error',
        loadChildren: () => import('./error/activation-error/module').then((m: typeof import('./error/activation-error/module')) => m.ActivationErrorComponent)
    },
    {
        name: 'account-error.**',
        url: '/account-error',
        loadChildren: () => import('./error/account-error/module').then((m: typeof import('./error/account-error/module')) => m.AccountErrorModule)
    },
    {
        name: 'account-upgrade-error.**',
        url: '/account-upgrade-error',
        loadChildren: () => import('./error/account-upgrade-error/module').then((m: typeof import('./error/account-upgrade-error/module')) => m.AccountUpgradeErrorModule)
    },
    { name: 'standalone-cpc.landing', url: '' },
    {
        name: 'international',
        abstract: true,
        url: '/international',
        loadChildren: () => import('./international/module').then((m: typeof import('./international/module')) => m.InternationalModule)
    },
    { name: 'international.landing', url: '' },
    {
        name: 'business-international',
        abstract: true,
        url: '/business-international',
        resolve: [
            {
                token: 'cimaCheck',
                deps: [ CimaUrl, CimaToken, StateService ],
                resolveFn: AuthGuard
            }
        ],
        loadChildren: () => import('./international/module').then((m: typeof import('./international/module')) => m.InternationalModule)
    },
    { name: 'business-international.landing', url: '' },
    {
        name: 'quickpay.**',
        url: '/quickpay',
        loadChildren: () => import('./quickpay/module').then((m: typeof import('./quickpay/module')) => m.QuickpayModule)
    },
    {
        name: 'watch.**',
        url: '/watches',
        loadChildren: () => import('./watches/module').then((m: typeof import('./watches/module')) => m.WatchModule)
    },
    {
        name: 'esim.**',
        url: '/esim',
        loadChildren: () => import('./esim/module').then((m: typeof import('./esim/module')) => m.EsimModule)
    },
    {
        name: 'now.**',
        url: '/now',
        loadChildren: () => import('./now-mobile/module').then((m: typeof import('./now-mobile/module')) => m.NowMobileModule)
    },
    {
        name: 'now-account-error.**',
        url: '/now-account-error',
        loadChildren: () => import('./error/now-account-error/module').then((m: typeof import('./error/now-account-error/module')) => m.NowAccountErrorModule)
    },
    {
        name: 'e911.**',
        url: '/geminiActivation',
        loadChildren: () => import('./e911/module').then((m: typeof import('./e911/module')) => m.E911Module)
    },
    {
        name: 'authorization-error.**',
        url: '/authorization-error',
        loadChildren: () => import('./error/authorization-error/module').then((m: typeof import('./error/authorization-error/module')) => m.AuthorizationErrorModule)
    }
];
